import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Eye, Code2, FileEdit, Globe, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useSite } from '../../hooks/useSiteConfig';

const MODES = [
  { id: 'coded', label: 'Coded', icon: Code2, hint: 'Original hand-coded page' },
  { id: 'builder', label: 'Live', icon: Globe, hint: 'Published builder page' },
  { id: 'draft', label: 'Draft', icon: FileEdit, hint: 'Unpublished builder draft' },
];

// Floating admin-only switch on public pages: flips between coded / live builder / draft views (via ?cms=…) without editing the URL.
export const CmsAuditToggle = ({ path }) => {
  const { user } = useAuth();
  const { builderPaths } = useSite();
  const location = useLocation();
  const navigate = useNavigate();
  const [hidden, setHidden] = React.useState(() => sessionStorage.getItem('cms_audit_hidden') === '1');
  if (!user?.is_admin || hidden) return null;

  const params = new URLSearchParams(location.search);
  const forced = params.get('cms');
  const live = builderPaths?.includes(path) ? 'builder' : 'coded';
  const current = forced || live;
  const go = (id) => {
    const next = new URLSearchParams(location.search);
    if (id === live) next.delete('cms'); else next.set('cms', id);
    navigate({ pathname: location.pathname, search: next.toString() ? `?${next}` : '' }, { replace: true });
  };

  return (
    <div className="fixed bottom-4 left-4 z-[90] flex items-center gap-1 rounded-full bg-neutral-900/95 text-white shadow-xl backdrop-blur px-2 py-1.5 text-xs" data-testid="cms-audit-toggle">
      <span className="flex items-center gap-1 pl-1.5 pr-1 text-neutral-400"><Eye className="w-3.5 h-3.5" />View</span>
      {MODES.map(({ id, label, icon: I, hint }) => (
        <button key={id} onClick={() => go(id)} title={hint} data-testid={`cms-audit-${id}`}
          className={`flex items-center gap-1 rounded-full px-2.5 py-1 font-medium transition-colors ${current === id ? 'bg-amber-500 text-neutral-900' : 'hover:bg-white/10 text-neutral-200'}`}>
          <I className="w-3.5 h-3.5" />{label}{id === live && <span className="opacity-60">·live</span>}
        </button>
      ))}
      <button onClick={() => { sessionStorage.setItem('cms_audit_hidden', '1'); setHidden(true); }} className="ml-1 p-1 rounded-full hover:bg-white/10 text-neutral-400" title="Hide for this session" data-testid="cms-audit-hide" aria-label="Hide"><X className="w-3.5 h-3.5" /></button>
    </div>
  );
};
